import React, {FC, useCallback, useState} from 'react';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {AsyncHandlerOf, Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {TextBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroupSpacer} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {SameWidthButton} from '../../../../../libs/shared/ui/src/components/buttons/sameWidthButton';
import {Dropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownHeader} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {isTeammateNamespace} from '../../../../helpers/models/namespaceHelpers';
import {CardGroupModel} from '../../../../models/api/cardGroupModel';
import {PermissionsEnum} from '../../../../models/api/roleModel';
import {HttpResponseError} from '../../../../services/http/httpError';
import {CreateCardGroupParams} from '../../../../store/data/cardGroups/cardGroupsActions';
import {DialogButtonGroup} from '../../dialogs/dialogButtonGroup';
import {DialogContent} from '../../dialogs/dialogContent';
import {NamespaceDropdownBoxContainer} from '../namespace/namespaceDropdownBoxContainer';

/*
 * Constants.
 */

const dropdownWidth = 260;
const accessDropdownHeight = 300;

enum CreateNewGroupErrorsEnum {
  NAME = 'name',
  GENERIC = 'generic',
}

/*
 * Props.
 */

interface CreateNewGroupDropdownProps {
  popoverProps: PopoverTransferProps;
  namespace: string;
  createCardGroupAsync: AsyncHandlerOf<CreateCardGroupParams, CardGroupModel>;
  onClose: Handler;
  onGroupCreated: HandlerOf<CardGroupModel>;
}

/*
 * Component.
 */

export const CreateNewGroupDropdown: FC<CreateNewGroupDropdownProps> = (props) => {
  const {popoverProps, namespace: passedInNamespace, onClose, createCardGroupAsync, onGroupCreated} = props;

  const [name, setName] = useState('');
  const [namespace, setNamespace] = useState(passedInNamespace);
  const [isCreating, setIsCreating] = useState(false);
  const [groupCreateError, setGroupCreateError] = useState<CreateNewGroupErrorsEnum>();

  const shouldDisableCreate = !name || !namespace;

  const onCreate = useCallback(async () => {
    // Clear any existing errors.
    setGroupCreateError(undefined);
    setIsCreating(true);

    try {
      const cardGroup = await createCardGroupAsync({name, namespace});
      onGroupCreated(cardGroup);
    } catch (error) {
      if (error instanceof HttpResponseError && error.status === 409) {
        setGroupCreateError(CreateNewGroupErrorsEnum.NAME);
      } else {
        setGroupCreateError(CreateNewGroupErrorsEnum.GENERIC);
      }
    }
    setIsCreating(false);
  }, [name, namespace, createCardGroupAsync, onGroupCreated]);

  const onCloseHandler = () => {
    // Prevent closing while saving.
    if (isCreating) {
      return;
    }
    onClose();
  };

  return (
    <Dropdown {...popoverProps} width={dropdownWidth} onRequestClose={onCloseHandler} hasBackground>
      <DropdownHeader title={<Trans id="create-card-group-dropdown-new-group">New list</Trans>} />
      <DialogContent>
        <TextBox
          label={<Trans id="create-card-group-dropdown-new-group-name">Name</Trans>}
          value={name}
          hasError={Boolean(groupCreateError)}
          errorMessage={maybeRenderCreateError(groupCreateError)}
          onChange={(value) => setName(value)}
          shouldAutoFocus
          isRequired
        />
        <NamespaceDropdownBoxContainer
          placeholder={
            <Trans id="create-card-group-dropdown-group-access-placeholder">
              Select the visibility of the contact list
            </Trans>
          }
          resourceNamespace={passedInNamespace}
          selectedNamespace={namespace}
          onNamespaceSelect={setNamespace}
          permission={PermissionsEnum.EDIT_CARD_GROUP}
          isDisplayOnly={isTeammateNamespace(passedInNamespace)}
          messageForDisplayOnly={
            <Trans id="create-card-group-dropdown-group-access-message">
              Private contacts can only be added to private lists
            </Trans>
          }
          header={<Trans id="create-card-group-dropdown-group-access-header">Access</Trans>}
          maxHeight={accessDropdownHeight}
          shouldRenderSharedDropdownItem
          shouldRenderVisibilityDescription
          shouldSkipTeamCheck
          isRequired
        />
      </DialogContent>
      <DialogButtonGroup>
        <ButtonGroupSpacer />
        <SameWidthButton
          onClick={onCloseHandler}
          isDisabled={isCreating}
          interactionId="contact-details-create-new-group-cancel-btn"
        >
          <Trans id="create-card-group-dropdown-group-cancel-button">Cancel</Trans>
        </SameWidthButton>
        <SameWidthButton
          variant={ButtonVariantsEnum.PRIMARY}
          isDisabled={shouldDisableCreate || isCreating}
          onClick={onCreate}
          interactionId="contact-details-create-new-group-create-btn"
        >
          <Trans id="create-card-group-dropdown-group-create-button">Create</Trans>
        </SameWidthButton>
      </DialogButtonGroup>
    </Dropdown>
  );
};

/*
 * Helpers.
 */

function maybeRenderCreateError(error?: CreateNewGroupErrorsEnum) {
  if (error === CreateNewGroupErrorsEnum.NAME) {
    return <Trans id="create-card-group-dropdown-error-conflict">This list already exists</Trans>;
  }
  if (error === CreateNewGroupErrorsEnum.GENERIC) {
    return <Trans id="create-card-group-dropdown-generic-error">An unexpected error occurred</Trans>;
  }
  return null;
}
